/**
 * The report's blocks, set as the app sets text.
 *
 * The third dresser of one document. `reportDocument` in `core/reportSections.ts`
 * decides every section, its order and every sentence in it; `markdown.ts` escapes
 * those blocks for Markdown and `pdfReport.ts` sets them in type, and this sets
 * them in the Overview tab. It holds no sentence of its own and drops none — a
 * block kind added to the union stops the build here (the switch has no
 * `default`) until somebody decides how the app shows it, which is how the three
 * readings stay one document rather than three that drifted apart one edit at a
 * time, as the Markdown and the PDF once did (D63).
 *
 * Presentation only, and that is the whole rule. The header's counts become
 * figures and the chips take the app's own tints; the words are the report's.
 *
 * NAMES ARE TEXT. A claim in the brief is named as the report names it — its
 * section number and label, "1.2 Shielding is forbidden" — and a `TextRun` carries
 * no node id, so nothing here can press through to the node without parsing that
 * text back, which would be a guess wearing a button. A pressable name needs the
 * id on the run, in the one derivation, where the Markdown and the PDF would carry
 * it too.
 */

import type { CSSProperties, ReactElement, ReactNode } from 'react'
import type { Block, ChipTone, Rich, SourceLine, TextRun } from '../core/reportSections'

/**
 * The report's five chip tones in the app's own hues, matched to what each tone
 * marks there: a reading's chip takes the tint the node card's reading chip wears
 * (`READING_TINT` in `Grounding.tsx` — supported the signal hue, mixed the gap
 * hue, refuted the loud one, the rest the weak grey), a bearing follows the same
 * three, and `purple`, which the report gives a pair's end in another exploration,
 * takes the synthesis hue the connections section wears. A chip always carries its
 * words, so no tint is ever the only thing saying what it is.
 */
const TONE: Record<ChipTone, string> = {
  teal: 'var(--sem-signal)',
  amber: 'var(--sem-gap)',
  purple: 'var(--sem-insight)',
  red: 'var(--sem-refuted)',
  gray: 'var(--sem-weak)',
}

function Run({ run }: { run: TextRun }): ReactElement | string {
  switch (run.style ?? 'plain') {
    case 'plain':
      return run.text
    case 'bold':
      return <b>{run.text}</b>
    case 'italic':
      return <i>{run.text}</i>
    case 'quiet':
      return <span className="rb-quiet">{run.text}</span>
    case 'mono':
      return <span className="rb-mono">{run.text}</span>
    case 'link':
      return <span className="rb-link">{run.text}</span>
  }
}

export function RichText({ rich }: { rich: Rich }) {
  return (
    <>
      {rich.map((run, i) => (
        <Run key={i} run={run} />
      ))}
    </>
  )
}

/** A run of rich text as one plain line — for a fold's preview, where emphasis
 *  would be noise and a line break would break the clamp. */
export function plainText(rich: Rich): string {
  return rich
    .map((r) => r.text)
    .join('')
    .replace(/\s*\n+\s*/g, ' ')
    .trim()
}

/** A kept source: the title as a link where the record has an address, the host
 *  and how it was read beneath — the node card's `.srcs` list, so a source reads
 *  the same in the brief as on the claim it was kept for. A passage from the
 *  reader's own document has no address, and none is invented for it. */
function Source({ s }: { s: SourceLine }) {
  return (
    <li>
      {s.url ? (
        <a href={s.url} target="_blank" rel="noreferrer noopener">
          {s.title}
        </a>
      ) : (
        <b>{s.title}</b>
      )}
      <span className="srcs-meta">{s.note}</span>
    </li>
  )
}

/**
 * The header's counts: the ones that are counts ("12 claims") as figures, the
 * rest ("Framed as a question", the date) as one quiet line above them. Split on
 * the text's own leading number, so the figure and its words are exactly the
 * report's; nothing is recounted here.
 */
export function Counts({ items }: { items: { text: string }[] }) {
  const figure = (t: string) => /^(\d[\d,]*)\s+(.+)$/.exec(t)
  const figures = items.map((i) => figure(i.text)).filter((m): m is RegExpExecArray => !!m)
  const rest = items.filter((i) => !figure(i.text))
  return (
    <div className="rb-counts">
      {rest.length > 0 && <p className="rb-counts-meta">{rest.map((i) => i.text).join(' · ')}</p>}
      {figures.length > 0 && (
        <ul className="rb-figures">
          {figures.map((m) => (
            <li key={m[0]}>
              {/* The space is for copy and for a screen reader: the flex column
                  drops it from the layout, and "12 claims" stays one phrase. */}
              <b className="num">{m[1]}</b> <span>{m[2]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

type Level = 2 | 3 | 4 | 5 | 6

function Heading({
  level,
  className,
  children,
}: {
  level: Level
  className: string
  children: ReactNode
}) {
  const H = `h${level}` as const
  return <H className={className}>{children}</H>
}

function OneBlock({ block: b, level }: { block: Block; level: Level }): ReactElement {
  switch (b.b) {
    case 'eyebrow':
      return <span className="eyebrow">{b.text}</span>
    case 'title':
      return (
        <Heading level={2} className="rb-title">
          {b.text}
        </Heading>
      )
    case 'lede':
      return (
        <p className="rb-lede">
          <RichText rich={b.rich} />
        </p>
      )
    case 'counts':
      return <Counts items={b.items} />
    case 'h2':
      return (
        <Heading level={Math.max(2, level - 1) as Level} className="rb-h2">
          {b.text}
        </Heading>
      )
    case 'h3':
      return (
        <Heading level={level} className="rb-h3">
          {b.text}
        </Heading>
      )
    case 'para':
      return (
        <p className="rb-para" data-tone={b.tone === 'quiet' ? 'quiet' : undefined}>
          <RichText rich={b.rich} />
        </p>
      )
    case 'bullet':
      // Grouped into lists by `ReportBlocks`; one alone still reads as a list.
      return (
        <ul className="rb-list">
          <Bullet block={b} />
        </ul>
      )
    case 'quotePanel':
      return (
        <div className="rb-quote">
          {b.paras.map((p, i) => (
            <p key={i}>
              <RichText rich={p} />
            </p>
          ))}
        </div>
      )
    case 'card':
      return (
        <div className="rb-card">
          <div className="rb-card-head">
            <b className="rb-card-title">{b.title}</b>
            {b.score && (
              <span className="rb-card-score" title={b.scoreCaption}>
                {b.scoreCaption && <span>{b.scoreCaption}</span>}
                <b className="num">{b.score}</b>
              </span>
            )}
          </div>
          {b.chips.length > 0 && (
            <div className="rb-chips">
              {b.chips.map((c, i) => (
                <span
                  key={i}
                  className="rb-chip"
                  style={{ '--c-tint': TONE[c.tone] } as CSSProperties}
                >
                  <i aria-hidden />
                  {c.text}
                </span>
              ))}
            </div>
          )}
          {b.paras.map((p, i) => (
            <p key={i} className="rb-card-para">
              <RichText rich={p} />
            </p>
          ))}
          {b.sources && b.sources.length > 0 && (
            <ul className="srcs">
              {b.sources.map((s, i) => (
                <Source key={i} s={s} />
              ))}
            </ul>
          )}
        </div>
      )
    case 'table':
      // Its own horizontal scroll: a calibration table is three columns of
      // figures, and the column it sits in can be a phone's width.
      return (
        <div className="rb-tablewrap">
          <table className="rb-table">
            <thead>
              <tr>
                {b.head.map((h, i) => (
                  <th key={i} scope="col">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((r, i) => (
                <tr key={i}>
                  {r.map((c, j) => (
                    <td key={j} className="num">
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'ledger':
      return (
        <div className="rb-ledger">
          <span className="eyebrow">{b.title}</span>
          <ul className="rb-list">
            {b.bullets.map((x, i) => (
              <li key={i}>
                <RichText rich={x} />
              </li>
            ))}
          </ul>
        </div>
      )
    case 'rule':
      return <hr className="hairline" />
    case 'footer':
      return <p className="rb-footer">{b.text}</p>
  }
}

function Bullet({ block }: { block: Extract<Block, { b: 'bullet' }> }) {
  return (
    <li>
      <RichText rich={block.rich} />
      {block.sub && block.sub.length > 0 && (
        <ul className="rb-sub">
          {block.sub.map((x, i) => (
            <li key={i}>
              <RichText rich={x} />
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * A run of blocks. Consecutive bullets are one list — the Markdown renderer's
 * own rule — so a screen reader hears "list, 12 items" rather than twelve lists
 * of one.
 *
 * `level` is the heading level an `h3` block takes here, and an `h2` one above
 * it: the same document sits under the tab's title in the preamble and under a
 * section's own heading in a fold, and a heading's level is where it sits.
 */
export function ReportBlocks({ blocks, level = 4 }: { blocks: Block[]; level?: Level }) {
  const out: ReactNode[] = []
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]!
    if (b.b !== 'bullet') {
      out.push(<OneBlock key={i} block={b} level={level} />)
      continue
    }
    const start = i
    const items: ReactNode[] = []
    while (i < blocks.length && blocks[i]!.b === 'bullet') {
      items.push(<Bullet key={i} block={blocks[i] as Extract<Block, { b: 'bullet' }>} />)
      i++
    }
    i--
    out.push(
      <ul key={start} className="rb-list">
        {items}
      </ul>,
    )
  }
  return <>{out}</>
}
